import { Box, Heading, Skeleton, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { CalculationTerminologiesTable } from './CalculationTerminologiesTable';
import { getInitialCalcTermsRows } from './calculationTerminologiesUtils';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export const CalculationTerminologiesContainer: React.FC<{
  organizationId: string;
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug'>[];
}> = withLDConsumer()(({ flags, organizationId, creditFacilities }) => {
  const toast = useToast();
  const history = useHistory();

  const {
    data: creditAgreementFullArtifactsData,
    loading: isCreditAgreementFullArtifactsLoading,
  } = useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery({
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });

  if (
    !creditAgreementFullArtifactsData ||
    isCreditAgreementFullArtifactsLoading
  ) {
    return <Skeleton height="700px" />;
  }

  const {
    organization: { currentCreditAgreementArtifacts },
  } = creditAgreementFullArtifactsData;

  let parsedCreditAgreementFullArtifacts = null;

  try {
    parsedCreditAgreementFullArtifacts = _.map(
      currentCreditAgreementArtifacts,
      (creditAgreementFullArtifact) => {
        const { creditAgreement, id } = creditAgreementFullArtifact;
        const parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
        finleyAssert(
          parsedCreditAgreement,
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
        );
        return parsedCreditAgreement;
      },
    );
  } catch (err) {
    Sentry.captureException(err);
    const errorToastTitle = `Error parsing credit agreement artifacts for organization ${organizationId}`;
    toast({
      ...errorToastConfig,
      title: errorToastTitle,
      id: 'errorParsingCreditAgreement',
    });
    history.push('/');
  }

  const calcTermsTableRows = getInitialCalcTermsRows({
    creditAgreementArtifacts: parsedCreditAgreementFullArtifacts,
    creditFacilities,
  });

  const orderedCalcTermsTableRows = _.orderBy(
    calcTermsTableRows,
    'termKey',
    'asc',
  );

  return (
    <Box>
      <Heading as="h1" fontSize="36px" fontWeight={600} mb="16px">
        Calculation Terminologies
      </Heading>
      <Text fontSize="16px" color={colors.black[70]} mb="32px">
        Finley&apos;s definition of the Calculation Terminologies.
      </Text>

      <CalculationTerminologiesTable
        calculationTerminologiesTableRows={orderedCalcTermsTableRows}
      />
    </Box>
  );
});
